import {
  ArrowLeftOutlined,
  CheckOutlined,
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Col, Modal, Row, Switch, Tooltip } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled, { createGlobalStyle } from "styled-components";

import {
  AI_QUOTA_PAYWALL_CONTACT_SALES_URL,
  AI_QUOTA_PAYWALL_CREDITS_PER_SEAT,
  AI_QUOTA_PAYWALL_ENTERPRISE_FEATURES,
  AI_QUOTA_PAYWALL_ENTERPRISE_PLATFORM_FEATURES,
  AI_QUOTA_PAYWALL_ENTERPRISE_SUPPORT_FEATURES,
  AI_QUOTA_PAYWALL_MAX_SEATS,
  AI_QUOTA_PAYWALL_MIN_SEATS,
  AI_QUOTA_PAYWALL_REASON_CONFIG,
  AI_QUOTA_PAYWALL_STRIPE_CHECKOUT_URL,
  AI_QUOTA_PAYWALL_TEAMS_FEATURES,
  AI_QUOTA_PAYWALL_TEAMS_PLATFORM_FEATURES,
  BillingPlan,
  isEnterpriseLikePlan,
} from "@superblocksteam/shared";
import type {
  AiQuotaPaywallReason as AiQuotaReason,
  OrganizationUserDto,
} from "@superblocksteam/shared";

import { Button } from "components/ui/Button";
import { MANAGE_BILLING } from "constants/rbac";
import { useAuthorizationCheck } from "hooks/ui/rbac/useAuthorizationCheck";
import {
  selectOnlyOrganization,
  selectOnlyOrganizationId,
} from "store/slices/organizations";
import { callServer, HttpMethod } from "store/utils/client";
import logger from "utils/logger";
import { sendErrorUINotification } from "utils/notification";

import { ManagePlanModal } from "../../dashboard/pages/Usage/ManagePlanModal";
import { useDefaultPricing } from "./useDefaultPricing";

export type AiQuotaPaywallModalBaseProps = {
  open: boolean;
  reason: AiQuotaReason | undefined;
  onClose: () => void;
  analyticsSurface: string;
  analyticsMetadata?: Record<string, unknown>;
  /** Called when user successfully completes an action (e.g. assigns seat). Use to navigate back to app instead of onClose. */
  onSuccess?: () => void;
  /** Override the Stripe checkout return URL (defaults to "/usage") */
  returnTo?: string;
  /** Override the modal header title */
  titleOverride?: string;
  /**
   * When true, skip the auto-close effect that fires when the billing plan
   * is not TRIAL or TEAMS.  Use this when the caller already validated the
   * quota on the server and wants to show the paywall regardless of the
   * client-side billing plan (which may lag behind due to caching).
   */
  skipPlanCheck?: boolean;
};

const ModalTitleText = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const ModalTitleDivider = styled.span`
  width: 1px;
  height: 16px;
  background: ${(props) => props.theme.colors.GREY_200};
  margin: 0 16px;
  flex-shrink: 0;
`;

const ModalTitleSubtext = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.GREY_500};
`;

const PaywallModalHeaderStyle = createGlobalStyle`
  .ai-paywall-modal.ant-modal-wrap .ant-modal {
    max-height: 100vh;
    padding: 24px 0;
    box-sizing: border-box;
  }
  .ai-paywall-modal.ant-modal-wrap .ant-modal-content {
    max-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .ai-paywall-modal.ant-modal-wrap .ant-modal-header {
    height: 48px;
    min-height: 48px;
    padding: 16px 24px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
  .ai-paywall-modal.ai-paywall-modal-seat-config.ant-modal-wrap .ant-modal-header {
    padding: 16px 12px;
  }
  .ai-paywall-modal.ant-modal-wrap .ant-modal-header .ant-modal-title {
    flex: 1;
    display: flex;
    justify-content: flex-start;
  }
  .ai-paywall-modal.ai-paywall-modal-seat-config.ant-modal-wrap .ant-modal-header .ant-modal-title {
    justify-content: center;
  }
  .ai-paywall-modal.ant-modal-wrap .ant-modal-close .ant-modal-close-icon {
    vertical-align: 2px;
  }
  .ai-paywall-modal.ant-modal-wrap .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
`;

const SeatConfigTitleRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const SeatConfigTitleCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: #697b8c;
  cursor: pointer;
  font-size: 16px;
  width: 24px;
  height: 24px;

  &:hover {
    color: ${(props) => props.theme.colors.GREY_900};
  }
`;

const PlanCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 9px;
  border: 1px solid ${(props) => props.theme.colors.GREY_100};
  background: ${(props) => props.theme.colors.WHITE};
  padding: 12px;
  height: 100%;

  &[data-highlighted="true"] {
    border: 1px solid ${(props) => props.theme.colors.GREY_100};
  }
`;

const PlanTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PlanName = styled.span`
  font-size: 17px;
  font-weight: 500;
  line-height: 150%;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
`;

const PlanPrice = styled.span`
  font-size: 33px;
  font-weight: 500;
  line-height: 1.1;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const PlanPriceUnit = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.GREY_500};
`;

const AnnualToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const Save20Text = styled.span`
  color: #079d89;
`;

const AnnualSwitchWrapper = styled.span`
  .ant-switch-checked {
    background: ${(props) => props.theme.colors.GREY_900} !important;
  }
  .ant-switch .ant-switch-handle,
  .ant-switch .ant-switch-handle::before {
    border: none !important;
  }
`;

const PlanDescription = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.GREY_600};
`;

const PlanCardTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PlanCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0;
`;

const CTAButton = styled(Button).attrs({ className: "ai-paywall-text-12" })`
  width: 100%;
  height: 32px;
  border-radius: 7px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  margin-bottom: 0;
  margin-top: 0;
  border: 1px solid ${(props) => props.theme.colors.GREY_200};
  color: ${(props) => props.theme.colors.GREY_700};
  background: ${(props) => props.theme.colors.WHITE};

  &[data-primary="true"] {
    border: 1px solid ${(props) => props.theme.colors.GREY_900};
    color: ${(props) => props.theme.colors.WHITE};
    background: ${(props) => props.theme.colors.GREY_900};
  }
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeatureSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.GREY_600};
`;

const IncludesDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;
  color: #a4adb7;
  font-weight: 400;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${(props) => props.theme.colors.GREY_100};
  }
`;

const SecondScreenBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SeatConfigBodyTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SeatConfigCard = styled.div`
  border: 1px solid ${(props) => props.theme.colors.GREY_100};
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 0;
`;

const SeatConfigTop = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.GREY_100};
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.GREY_900};
  text-align: center;
`;

const SeatConfigMiddle = styled.div`
  height: 56px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StepperButton = styled.button`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.colors.GREY_200};
  background: ${(props) => props.theme.colors.WHITE};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.GREY_700};

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SeatCount = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.GREY_900};
  min-width: 48px;
  text-align: center;
`;

const SeatConfigBottom = styled.div`
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceSummaryRow = styled.div`
  height: 33px;
  box-sizing: border-box;
  border-top: 1px solid ${(props) => props.theme.colors.GREY_100};
  padding: 16px 2px 0 2px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PriceSummaryLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #48515b;
`;

const PriceSummaryTotal = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
`;

const PriceSummaryTotalAmount = styled.span`
  font-size: 24px;
  font-weight: 500;
  line-height: 100%;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const PriceSummaryTotalUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const CheckoutButton = styled.button`
  width: 100%;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: ${(props) => props.theme.colors.GREY_900};
  color: ${(props) => props.theme.colors.WHITE};
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FeatureTextUnderline = styled.a`
  text-decoration: underline;
  color: ${(props) => props.theme.colors.GREY_500};
`;

const AI_CREDITS_PACKS_DOCS_URL =
  "https://docs.superblocks.com/building-with-clark/ai-credits";

const ORG_ROLE_OWNER = "Owner";
const ORG_ROLE_ADMIN = "Admin";

function getOrgAdminOrOwnerEmail(users: OrganizationUserDto[]): string | null {
  const withRole = (roleName: string) =>
    users.find((u) => u.assignments?.some((a) => a.role?.name === roleName));
  const owner = withRole(ORG_ROLE_OWNER);
  if (owner?.email) return owner.email;
  const admin = withRole(ORG_ROLE_ADMIN);
  return admin?.email ?? null;
}

const CONTACT_ADMIN_SUBJECT = "Request to upgrade our Superblocks AI plan";
const CONTACT_ADMIN_BODY =
  "Hi,\n\nI need help upgrading our organization's AI plan to continue using Clark AI. Could you please look into this?\n\nThanks.";

export function AiQuotaPaywallModalBase({
  open,
  reason,
  onClose,
  analyticsSurface,
  analyticsMetadata,
  onSuccess,
  returnTo: returnToProp,
  titleOverride,
  skipPlanCheck,
}: AiQuotaPaywallModalBaseProps) {
  const organization = useSelector(selectOnlyOrganization);
  const organizationId = useSelector(selectOnlyOrganizationId);
  const [canManageBilling] = useAuthorizationCheck([MANAGE_BILLING]);
  const defaultPricing = useDefaultPricing();
  const billingPlan = organization?.billing?.plan;
  const stripeSubStatus = organization?.billing?.stripeSubStatus;
  const isTrialing =
    billingPlan === BillingPlan.TEAMS && stripeSubStatus === "trialing";
  const isPastDue =
    billingPlan === BillingPlan.TEAMS && stripeSubStatus === "past_due";
  // Past-due routes to ManagePlanModal regardless of `reason`, because the
  // dedicated "Update payment method" UI lives there. Without this, a
  // payment_past_due reason on a TEAMS+past_due org would still hit the
  // generic paywall flow.
  const shouldShowPaywallModal =
    !isPastDue && (billingPlan === BillingPlan.TRIAL || isTrialing);
  const shouldUseManagePlanModal =
    billingPlan === BillingPlan.TEAMS && !isTrialing;
  const isEnterprise = isEnterpriseLikePlan(billingPlan);
  const shouldCloseUnsupportedPlan =
    !skipPlanCheck &&
    open &&
    billingPlan != null &&
    !shouldShowPaywallModal &&
    !shouldUseManagePlanModal &&
    !isEnterprise;

  const [view, setView] = useState<"plan-selection" | "seat-config">(
    "plan-selection",
  );
  const orgInterval = organization?.billing?.billingInterval;
  const [seats, setSeats] = useState(1);
  const [isAnnual, setIsAnnual] = useState(orgInterval !== "monthly");
  const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);
  const [isEndingTrial, setIsEndingTrial] = useState(false);

  useEffect(() => {
    if (!open) {
      setView("plan-selection");
      setIsRedirectingToCheckout(false);
      setSeats(1);
      setIsAnnual(orgInterval !== "monthly");
    }
  }, [open, orgInterval]);

  useEffect(() => {
    if (!open) {
      return;
    }

    logger.info("[AiQuota] Paywall modal shown", {
      reason: reason ?? "unknown",
      surface: analyticsSurface,
      ...analyticsMetadata,
    });
  }, [analyticsMetadata, analyticsSurface, open, reason]);

  useEffect(() => {
    if (!shouldCloseUnsupportedPlan) {
      return;
    }

    onClose();
  }, [onClose, shouldCloseUnsupportedPlan]);

  const onUpgrade = useCallback(async () => {
    if (isRedirectingToCheckout) {
      return;
    }
    setIsRedirectingToCheckout(true);
    try {
      const returnTo = returnToProp ?? "/usage";
      const response = await callServer<{ checkoutUrl: string }>(
        {
          method: HttpMethod.Post,
          url: "v1/billing/create-checkout-session",
          body: {
            returnTo,
            billingInterval: isAnnual ? "annual" : "monthly",
            builderSeats: seats,
            embedded: false,
          },
        },
        { notifyOnError: false },
      );
      window.location.assign(response.checkoutUrl);
    } catch (err) {
      logger.warn("Create checkout session failed, opening fallback URL", {
        message: err instanceof Error ? err.message : String(err),
      });
      window.open(AI_QUOTA_PAYWALL_STRIPE_CHECKOUT_URL, "_blank");
      setIsRedirectingToCheckout(false);
    }
  }, [seats, isAnnual, isRedirectingToCheckout, returnToProp]);

  const onEndTrial = useCallback(
    async (builderSeats?: number) => {
      if (isEndingTrial) return;
      setIsEndingTrial(true);
      try {
        await callServer<{ success: boolean }>(
          {
            method: HttpMethod.Post,
            url: "v1/billing/end-trial",
            body: {
              builderSeats: builderSeats ?? seats,
              billingInterval: isAnnual ? "annual" : "monthly",
            },
          },
          { notifyOnError: false },
        );
        window.location.reload();
      } catch (err) {
        logger.warn("End trial failed", {
          message: err instanceof Error ? err.message : String(err),
        });
        sendErrorUINotification({
          message: "Failed to convert to Teams. Please try again.",
        });
        setIsEndingTrial(false);
      }
    },
    [isEndingTrial, seats, isAnnual],
  );

  const onContactAdmin = useCallback(async () => {
    if (!organizationId) return;
    try {
      const users = await callServer<OrganizationUserDto[]>(
        {
          method: HttpMethod.Get,
          url: `v1/organizations/${organizationId}/users`,
        },
        { notifyOnError: false, shouldThrowHttpError: true },
      );
      const email = getOrgAdminOrOwnerEmail(users ?? []);
      if (email) {
        const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(CONTACT_ADMIN_SUBJECT)}&body=${encodeURIComponent(CONTACT_ADMIN_BODY)}`;
        window.open(mailto, "_blank");
      } else {
        sendErrorUINotification({
          message:
            "No organization admin or owner could be found. Ask your admin to upgrade your plan.",
        });
      }
    } catch (err: unknown) {
      const code =
        err && typeof err === "object" && "code" in err
          ? (err as { code: number }).code
          : null;
      if (code === 403) {
        sendErrorUINotification({
          message:
            "You don't have permission to view organization admins. Ask your admin to upgrade your plan.",
        });
      } else {
        sendErrorUINotification({
          message:
            "Could not load organization admins. Ask your admin to upgrade your plan.",
        });
      }
    }
  }, [organizationId]);

  // TRIAL → paywall modal, TEAMS → ManagePlanModal, else → no modal.
  // When skipPlanCheck is set the caller (e.g. deploy quota guard) already
  // validated the quota on the server, so we render the paywall regardless
  // of the client-side billing plan (which may lag behind).
  if (
    !skipPlanCheck &&
    !shouldShowPaywallModal &&
    !shouldUseManagePlanModal &&
    !isEnterprise
  ) {
    return null;
  }

  if (reason === "user_credit_limit_exceeded" && open) {
    return (
      <>
        <PaywallModalHeaderStyle />
        <Modal
          open={open}
          closable
          onCancel={onClose}
          footer={null}
          destroyOnClose
          centered
          width={480}
          wrapClassName="ai-paywall-modal"
          bodyStyle={{ padding: "32px 24px", textAlign: "center" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div style={{ fontSize: 40 }}>⚠️</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: "#1f2937" }}>
              You&apos;ve reached your assigned usage limit
            </div>
            {canManageBilling ? (
              <div
                style={{ fontSize: 13, lineHeight: "20px", color: "#6b7280" }}
              >
                You can increase your usage limit on the{" "}
                <a
                  href="/usage"
                  onClick={onClose}
                  style={{ color: "#0ea5e9", textDecoration: "underline" }}
                >
                  Usage page
                </a>
                .
              </div>
            ) : (
              <div
                style={{ fontSize: 13, lineHeight: "20px", color: "#6b7280" }}
              >
                Contact an admin to request an increase.
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 8,
                padding: "8px 24px",
                fontSize: 13,
                fontWeight: 500,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
                color: "#374151",
              }}
            >
              Close
            </button>
          </div>
        </Modal>
      </>
    );
  }

  if (shouldUseManagePlanModal) {
    return (
      <ManagePlanModal
        open={open}
        onClose={onClose}
        onSuccess={onSuccess}
        canManageBilling={canManageBilling}
        reason={reason}
      />
    );
  }

  if (isEnterprise && open) {
    const reasonConfig = reason ? AI_QUOTA_PAYWALL_REASON_CONFIG[reason] : null;
    const title =
      reasonConfig?.title ?? "Your organization has reached its AI usage limit";
    const subtitle = canManageBilling
      ? undefined
      : (reasonConfig?.subtitle ?? "Contact your admin for assistance.");
    return (
      <>
        <PaywallModalHeaderStyle />
        <Modal
          open={open}
          closable
          onCancel={onClose}
          footer={null}
          destroyOnClose
          centered
          width={480}
          wrapClassName="ai-paywall-modal"
          bodyStyle={{ padding: "32px 24px", textAlign: "center" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div style={{ fontSize: 40 }}>⚠️</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: "#1f2937" }}>
              {title}
            </div>
            {canManageBilling ? (
              <div
                style={{ fontSize: 13, lineHeight: "20px", color: "#6b7280" }}
              >
                Contact your account team to adjust your contract, or{" "}
                <a
                  href="/usage"
                  onClick={onClose}
                  style={{ color: "#0ea5e9", textDecoration: "underline" }}
                >
                  adjust overage settings on the usage page
                </a>
                .
              </div>
            ) : (
              <div
                style={{ fontSize: 13, lineHeight: "20px", color: "#6b7280" }}
              >
                {subtitle}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 8,
                padding: "8px 24px",
                fontSize: 13,
                fontWeight: 500,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
                color: "#374151",
              }}
            >
              Close
            </button>
          </div>
        </Modal>
      </>
    );
  }

  const expiryDate = organization?.billing?.expiryDate
    ? new Date(organization.billing.expiryDate)
    : null;
  const isTrialExpired =
    billingPlan === BillingPlan.TRIAL &&
    expiryDate != null &&
    expiryDate.getTime() < Date.now();
  const config = AI_QUOTA_PAYWALL_REASON_CONFIG.trial_expired;
  const title =
    titleOverride ??
    (isTrialing
      ? "Upgrade your plan now"
      : isTrialExpired
        ? config.title
        : "Upgrade your plan");

  const price = isAnnual
    ? defaultPricing.annualPrice / 12
    : defaultPricing.monthlyPrice;
  const totalPrice = seats * price;
  const totalPriceFormatted = totalPrice.toLocaleString("en-US");

  const annualToggle = (
    <AnnualToggleWrapper>
      <span>
        Annual <Save20Text>(Save 20%)</Save20Text>
      </span>
      <AnnualSwitchWrapper>
        <Switch size="small" checked={isAnnual} onChange={setIsAnnual} />
      </AnnualSwitchWrapper>
    </AnnualToggleWrapper>
  );

  const isCreditPacksFeature = (f: string) =>
    f.includes("AI Credit Packs") || f === "AI Credit Packs add-on available";

  const teamsFeaturesSection = (
    <FeaturesContainer>
      <IncludesDivider>Includes:</IncludesDivider>
      <FeatureSection>
        <SectionTitle>AI & Credits</SectionTitle>
        <FeatureList>
          {AI_QUOTA_PAYWALL_TEAMS_FEATURES.map((feature) => (
            <FeatureItem key={feature}>
              <CheckOutlined
                style={{ color: "#0099FF", fontSize: 12, marginTop: 2 }}
              />
              {isCreditPacksFeature(feature) ? (
                <>
                  <FeatureTextUnderline
                    href={AI_CREDITS_PACKS_DOCS_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    AI Credit Packs
                  </FeatureTextUnderline>
                  {" add-on available"}
                </>
              ) : (
                <span>{feature}</span>
              )}
            </FeatureItem>
          ))}
        </FeatureList>
      </FeatureSection>
      <FeatureSection>
        <SectionTitle>Platform</SectionTitle>
        <FeatureList>
          {AI_QUOTA_PAYWALL_TEAMS_PLATFORM_FEATURES.map((feature) => (
            <FeatureItem key={feature}>
              <CheckOutlined
                style={{ color: "#0099FF", fontSize: 12, marginTop: 2 }}
              />
              <span>{feature}</span>
            </FeatureItem>
          ))}
        </FeatureList>
      </FeatureSection>
    </FeaturesContainer>
  );

  const secondScreenFeaturesSections = (
    <>
      <FeatureSection>
        <SectionTitle>AI & Credits</SectionTitle>
        <FeatureList>
          {AI_QUOTA_PAYWALL_TEAMS_FEATURES.map((feature) => (
            <FeatureItem key={feature}>
              <CheckOutlined
                style={{ color: "#0099FF", fontSize: 12, marginTop: 2 }}
              />
              {isCreditPacksFeature(feature) ? (
                <>
                  <FeatureTextUnderline
                    href={AI_CREDITS_PACKS_DOCS_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    AI Credit Packs
                  </FeatureTextUnderline>
                  {" add-on available"}
                </>
              ) : (
                <span>{feature}</span>
              )}
            </FeatureItem>
          ))}
        </FeatureList>
      </FeatureSection>
      <FeatureSection>
        <SectionTitle>Platform</SectionTitle>
        <FeatureList>
          {AI_QUOTA_PAYWALL_TEAMS_PLATFORM_FEATURES.map((feature) => (
            <FeatureItem key={feature}>
              <CheckOutlined
                style={{ color: "#0099FF", fontSize: 12, marginTop: 2 }}
              />
              <span>{feature}</span>
            </FeatureItem>
          ))}
        </FeatureList>
      </FeatureSection>
    </>
  );

  const modalTitle = (
    <span style={{ display: "flex", alignItems: "center" }}>
      <ModalTitleText>{title}</ModalTitleText>
      <ModalTitleDivider />
      <ModalTitleSubtext>
        Please purchase a self-serve plan or contact sales to discuss enterprise
        pricing
      </ModalTitleSubtext>
    </span>
  );

  const seatConfigTitle = (
    <SeatConfigTitleRow>
      <BackButton
        type="button"
        onClick={() => setView("plan-selection")}
        aria-label="Back to plan selection"
      >
        <ArrowLeftOutlined />
      </BackButton>
      <SeatConfigTitleCenter>
        <ModalTitleText>{title}</ModalTitleText>
      </SeatConfigTitleCenter>
    </SeatConfigTitleRow>
  );

  const seatConfigContent = (
    <PlanCard data-highlighted="true">
      <SecondScreenBody>
        <SeatConfigBodyTop>
          <PlanTopRow>
            <PlanName>Teams</PlanName>
            {annualToggle}
          </PlanTopRow>
          <PlanCardContent>
            <PriceRow>
              <PlanPrice>${price}</PlanPrice>
              <PlanPriceUnit>/ AI Builder / month</PlanPriceUnit>
            </PriceRow>
            <PlanDescription>
              Everything you need to build and deploy internal tools.
            </PlanDescription>
            <SeatConfigCard>
              <SeatConfigTop data-test="seat-config-section">
                Choose number of AI Builder licenses
              </SeatConfigTop>
              <SeatConfigMiddle>
                <StepperButton
                  onClick={() =>
                    setSeats((s) => Math.max(AI_QUOTA_PAYWALL_MIN_SEATS, s - 1))
                  }
                  disabled={seats <= AI_QUOTA_PAYWALL_MIN_SEATS}
                  aria-label="Decrease licenses"
                >
                  <MinusOutlined style={{ fontSize: 16 }} />
                </StepperButton>
                <SeatCount>{seats}</SeatCount>
                <StepperButton
                  onClick={() =>
                    setSeats((s) => Math.min(AI_QUOTA_PAYWALL_MAX_SEATS, s + 1))
                  }
                  disabled={seats >= AI_QUOTA_PAYWALL_MAX_SEATS}
                  aria-label="Increase licenses"
                >
                  <PlusOutlined style={{ fontSize: 16 }} />
                </StepperButton>
              </SeatConfigMiddle>
              <SeatConfigBottom>
                <PriceSummaryRow>
                  <PriceSummaryLabel>
                    {seats} {seats === 1 ? "license" : "licenses"} × ${price}/mo
                  </PriceSummaryLabel>
                  <PriceSummaryTotal>
                    <PriceSummaryTotalAmount>
                      ${totalPriceFormatted}
                    </PriceSummaryTotalAmount>
                    <PriceSummaryTotalUnit>/mo</PriceSummaryTotalUnit>
                  </PriceSummaryTotal>
                </PriceSummaryRow>
                <a
                  href={AI_CREDITS_PACKS_DOCS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="block pb-3 text-xs leading-4 text-gray-500 underline hover:text-gray-700"
                >
                  Includes {seats * AI_QUOTA_PAYWALL_CREDITS_PER_SEAT}{" "}
                  {seats * AI_QUOTA_PAYWALL_CREDITS_PER_SEAT === 1
                    ? "credit"
                    : "credits"}{" "}
                  + 1 deployed app per team
                </a>
                <CheckoutButton
                  data-test="paywall-checkout-btn"
                  onClick={isTrialing ? () => onEndTrial(seats) : onUpgrade}
                  disabled={
                    isTrialing ? isEndingTrial : isRedirectingToCheckout
                  }
                >
                  {isTrialing
                    ? isEndingTrial
                      ? "Upgrading..."
                      : "Upgrade"
                    : isRedirectingToCheckout
                      ? "Redirecting..."
                      : "Checkout now"}
                </CheckoutButton>
              </SeatConfigBottom>
            </SeatConfigCard>
          </PlanCardContent>
        </SeatConfigBodyTop>
        {secondScreenFeaturesSections}
      </SecondScreenBody>
    </PlanCard>
  );

  if (view === "seat-config") {
    return (
      <>
        <PaywallModalHeaderStyle />
        <Modal
          open={open}
          title={seatConfigTitle}
          closable
          onCancel={onClose}
          footer={null}
          destroyOnClose
          centered
          width={492}
          wrapClassName="ai-paywall-modal ai-paywall-modal-seat-config"
          bodyStyle={{ padding: "16px" }}
        >
          <div data-test="paywall-seat-config">{seatConfigContent}</div>
        </Modal>
      </>
    );
  }

  const planSelectionContent = (
    <Row gutter={24}>
      <Col span={12}>
        <PlanCard data-highlighted="true">
          <PlanCardTop>
            <PlanTopRow>
              <PlanName>Teams</PlanName>
              {annualToggle}
            </PlanTopRow>
            <PlanCardContent>
              <PriceRow>
                <PlanPrice>${price}</PlanPrice>
                <PlanPriceUnit>/ AI Builder / month</PlanPriceUnit>
              </PriceRow>
              <PlanDescription>
                Everything you need to build and deploy internal tools.
              </PlanDescription>
              {canManageBilling ? (
                <CTAButton
                  data-primary="true"
                  type="default"
                  onClick={() => setView("seat-config")}
                >
                  {isTrialing ? "Upgrade now" : "Upgrade"}
                </CTAButton>
              ) : (
                <CTAButton
                  type="default"
                  onClick={onContactAdmin}
                  disabled={!organizationId}
                >
                  Contact your admin to upgrade
                </CTAButton>
              )}
            </PlanCardContent>
          </PlanCardTop>
          {teamsFeaturesSection}
        </PlanCard>
      </Col>

      <Col span={12}>
        <PlanCard>
          <PlanCardTop>
            <PlanTopRow>
              <PlanName>Enterprise</PlanName>
            </PlanTopRow>
            <PlanCardContent>
              <PriceRow>
                <PlanPrice>Custom</PlanPrice>
              </PriceRow>
              <PlanDescription>
                Advanced security, deployment, and support.
              </PlanDescription>
              <CTAButton
                type="default"
                onClick={() =>
                  window.open(AI_QUOTA_PAYWALL_CONTACT_SALES_URL, "_blank")
                }
              >
                Contact us
              </CTAButton>
            </PlanCardContent>
          </PlanCardTop>
          <FeaturesContainer>
            <IncludesDivider>Everything in Teams, plus:</IncludesDivider>
            <FeatureSection>
              <SectionTitle>AI Platform</SectionTitle>
              <FeatureList>
                {AI_QUOTA_PAYWALL_ENTERPRISE_FEATURES.map((feature) => (
                  <FeatureItem key={feature}>
                    <CheckOutlined
                      style={{
                        color: "#0099FF",
                        fontSize: 12,
                        marginTop: 2,
                      }}
                    />
                    {feature.startsWith("Source Control") ? (
                      <>
                        <span>Source Control</span>
                        <Tooltip title={feature}>
                          <InfoCircleOutlined
                            style={{
                              color: "#8c8c8c",
                              fontSize: 12,
                              marginLeft: 4,
                            }}
                          />
                        </Tooltip>
                      </>
                    ) : (
                      <span>{feature}</span>
                    )}
                  </FeatureItem>
                ))}
              </FeatureList>
            </FeatureSection>
            <FeatureSection>
              <SectionTitle>Security & Compliance</SectionTitle>
              <FeatureList>
                {AI_QUOTA_PAYWALL_ENTERPRISE_PLATFORM_FEATURES.map(
                  (feature) => (
                    <FeatureItem key={feature}>
                      <CheckOutlined
                        style={{
                          color: "#0099FF",
                          fontSize: 12,
                          marginTop: 2,
                        }}
                      />
                      <span>{feature}</span>
                    </FeatureItem>
                  ),
                )}
              </FeatureList>
            </FeatureSection>
            <FeatureSection>
              <SectionTitle>Support</SectionTitle>
              <FeatureList>
                {AI_QUOTA_PAYWALL_ENTERPRISE_SUPPORT_FEATURES.map((feature) => (
                  <FeatureItem key={feature}>
                    <CheckOutlined
                      style={{
                        color: "#0099FF",
                        fontSize: 12,
                        marginTop: 2,
                      }}
                    />
                    <span>{feature}</span>
                  </FeatureItem>
                ))}
              </FeatureList>
            </FeatureSection>
          </FeaturesContainer>
        </PlanCard>
      </Col>
    </Row>
  );

  return (
    <>
      <PaywallModalHeaderStyle />
      <Modal
        open={open}
        title={modalTitle}
        closable
        onCancel={onClose}
        footer={null}
        destroyOnClose
        centered
        width={776}
        wrapClassName="ai-paywall-modal"
        bodyStyle={{ padding: "16px" }}
      >
        <div data-test="paywall-plan-selection">{planSelectionContent}</div>
      </Modal>
    </>
  );
}
